import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Action, Actions, createSelector, State, StateContext } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ReferralsPortalApplication } from '@recruitee/http/types';

import { HttpApiService } from '../../../core/http-api.service';
import {
  EntityId,
  EntityState,
  EntityStateHelpers,
  EntityUpdateStrategy,
  IEntityState,
  PaginationMeta,
  paginationMetaState,
} from '../../../core/state';
import { dispatchReferencesByType } from '../../../core/state/entity-state-repos-map';
import { ReferralsApplicationRepo } from '../application/application.repository';
import {
  CreateConnection,
  DeleteConnection,
  LoadConnection,
  LoadConnections,
  UpdateConnection,
} from './connection.actions';
import { ReferralsConnection } from './connection.interfaces';

export type ConnectionState = IEntityState<ReferralsConnection, PaginationMeta>;

@State({
  name: 'connection',
  defaults: EntityStateHelpers.createDefaultState(paginationMetaState()),
})
@Injectable()
export class ReferralsPortalConnectionRepo extends EntityState<
  ReferralsConnection,
  PaginationMeta
>() {
  public static withRelations(): (...args: any) => Record<EntityId, ReferralsConnection> {
    return createSelector(
      [ReferralsPortalConnectionRepo.rawEntities, ReferralsApplicationRepo.withRelations()],
      (
        connections: Record<EntityId, ReferralsConnection>,
        applications: Record<EntityId, ReferralsPortalApplication>,
      ) => {
        const applicationsArr = Object.values(applications);
        return Object.entries(connections).reduce(
          (acc, [id, connection]) => ({
            ...acc,
            [id]: {
              ...connection,
              applications: applicationsArr.filter(
                application => application.referralConnectionId === connection.id,
              ),
            },
          }),
          {},
        );
      },
    );
  }
  constructor(private httpApi: HttpApiService, private actions$: Actions) {
    super(actions$, EntityUpdateStrategy.update);
  }

  @Action(LoadConnections)
  public loadConnections(
    ctx: StateContext<ConnectionState>,
    action: LoadConnections,
  ): Observable<ReferralsConnection[]> {
    const params = (action.payload && action.payload.params) || undefined;
    this.markAsLoading(ctx, action);
    return this.httpApi.get('/connections', params).pipe(
      tap(result => {
        if (action.payload && action.payload.clearStore) {
          this.resetStateToDefaults(ctx);
        }

        this.updateMetadata(ctx, {
          pageSize: result.meta.limit,
          pageIndex: result.meta.page,
          totalCount: result.meta.totalCount,
        });
      }),
      tap(result => {
        this.saveMany(ctx, action, result.connections);
        if (result.references && result.references.length)
          dispatchReferencesByType(ctx, result.references);
      }),
    );
  }

  @Action(LoadConnection)
  public loadConnection(
    ctx: StateContext<ConnectionState>,
    action: LoadConnection,
  ): Observable<ReferralsConnection> {
    this.markAsLoading(ctx, action);
    return this.httpApi.get(`/connections/${action.payload.id}`).pipe(
      tap(res => {
        this.save(ctx, action, res.connection);
        if (action.payload && action.payload.active) {
          this.markAsActive(ctx, res.connection.id);
        }
      }),
    );
  }

  @Action(CreateConnection)
  public createConnection(
    ctx: StateContext<ConnectionState>,
    action: CreateConnection,
  ): Observable<ReferralsConnection> {
    this.markAsLoading(ctx, action);
    return this.httpApi.post('/connections', null, { connection: action.payload }).pipe(
      map(result => result.connection),
      tap(
        connection => this.save(ctx, action, connection, { position: 'start' }),
        (error: HttpErrorResponse) => this.setError(ctx, action, error),
      ),
    );
  }

  @Action(DeleteConnection)
  public deleteConnection(
    ctx: StateContext<ConnectionState>,
    action: DeleteConnection,
  ): Observable<any> {
    const { id } = action.payload;
    this.markAsLoading(ctx, action);
    return this.httpApi.delete(`/connections/${id}`).pipe(tap(() => this.remove(ctx, id)));
  }

  @Action(UpdateConnection)
  public updateConnection(
    ctx: StateContext<ConnectionState>,
    action: UpdateConnection,
  ): Observable<ReferralsConnection> {
    const { id } = action.payload;
    this.markAsLoading(ctx, action);
    return this.httpApi.patch(`/connections/${id}`, null, { connection: action.payload }).pipe(
      map(result => result.connection),
      tap(connection => this.save(ctx, action, connection)),
    );
  }
}
